import { useEffect, useState, useRef } from "react";
import { api, formatApiError, API, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Trophy, Users, ClipboardList, ArrowUpRight, UserCog, Layers, Zap, ExternalLink, Camera, Trash2, Shield, Clock } from "lucide-react";
import { LiveNow } from "@/components/app/LiveNow";

const PLAN_LABEL = { presencia: "Plan Presencia", club: "Plan Club", total: "Plan Total" };

export default function ClubAdminDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [club, setClub] = useState(null);
  const [plans, setPlans] = useState([]);
  const [trial, setTrial] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [a, c, p] = await Promise.all([
          api.get("/analytics/club"),
          api.get("/clubs"),
          api.get("/plans"),
        ]);
        setData(a.data);
        const currentClub = c.data[0];
        setClub(currentClub);
        setPlans(p.data);
        if (currentClub?.id) {
          try { const t = await api.get(`/clubs/${currentClub.id}/trial`); setTrial(t.data); } catch (err) { /* trial info optional */ }
        }
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    })();
  }, []);

  const kpis = data?.kpis || { categorias: 0, equipos: 0, jugadores: 0, entrenadores: 0, jornadas: 0, partidos: 0, entrenos: 0 };
  const currentPlan = plans.find(p => p.id === club?.plan);
  const clubUrl = null;

  return (
    <div>
      <div className="mb-8">
        <div className="overline">Panel del club</div>
        <h1 className="font-heading font-black text-5xl md:text-6xl tracking-tighter uppercase mt-2">
          Buenos días, <span className="text-primary">{user?.name?.split(" ")[0]}</span>
        </h1>
        {club && (
          <div className="mt-6 flex items-start gap-5 flex-wrap">
            <ClubLogoUploader
              club={club}
              canEdit={user?.role === "club_admin" || user?.role === "super_admin"}
              onUpdated={(url) => setClub((c) => ({ ...c, logo_url: url }))}
            />
            <div className="flex-1 min-w-[200px] pt-2">
              <span className="font-heading font-bold uppercase tracking-tight text-base block">{club.name}</span>
              <div className="mt-2 flex items-center gap-3 flex-wrap text-xs">
                {currentPlan && (
                  <span className="font-heading font-bold uppercase tracking-tight text-primary">{PLAN_LABEL[currentPlan.id] || currentPlan.name}</span>
                )}
                {clubUrl && (
              <a href={`https://${clubUrl}`} target="_blank" rel="noreferrer" className="font-mono text-primary link-underline flex items-center gap-1">
                {clubUrl} <ExternalLink size={11} />
              </a>
            )}
              </div>
            </div>
          </div>
        )}
      </div>
      <LiveNow />

      {trial?.is_trial && (
        <div className={`mb-6 border-2 p-4 flex items-center justify-between gap-4 flex-wrap ${trial.days_left <= 3 ? "border-red-500 bg-red-500/10" : trial.days_left <= 7 ? "border-amber-400 bg-amber-400/10" : "border-blue-500 bg-blue-500/10"}`} data-testid="trial-banner">
          <div className="flex items-center gap-3">
            <Clock size={18} className={trial.days_left <= 3 ? "text-red-400" : trial.days_left <= 7 ? "text-amber-300" : "text-blue-300"} />
            <div>
              <div className="font-heading font-bold uppercase text-sm tracking-tight">
                {trial.days_left < 0 ? "Prueba gratuita finalizada" : trial.days_left === 0 ? "Último día de prueba" : `Prueba gratuita · ${trial.days_left} día${trial.days_left === 1 ? "" : "s"} restante${trial.days_left === 1 ? "" : "s"}`}
              </div>
              <div className="text-xs opacity-80 mt-0.5">
                {trial.days_left < 0
                  ? "Activa tu plan ahora para no perder el acceso a las estadísticas del club."
                  : `Al finalizar el 15º día podrás continuar con el precio de lanzamiento — te lo respetamos durante 24 meses.`}
              </div>
            </div>
          </div>
          <Link to="/billing" data-testid="trial-upgrade-btn" className="inline-flex items-center gap-2 h-9 px-4 bg-primary text-primary-foreground text-xs uppercase font-heading tracking-wider hover:bg-primary/80">
            Activar plan <ArrowUpRight size={12} />
          </Link>
        </div>
      )}

      {currentPlan && (
        <div className="mb-8 border border-border">
          <div className="p-4 border-b border-border flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Zap size={14} className="text-primary" />
              <span className="overline">Suscripción</span>
              <span className="font-heading font-bold uppercase tracking-tight text-sm">{currentPlan.label}</span>
            </div>
            <span className="text-[10px] font-mono text-muted-foreground uppercase">{club.plan_status === "active" ? "Activo" : club.plan_status}</span>
          </div>
          <div className="grid grid-tactical grid-cols-2 md:grid-cols-4">
            <UsageBar label="Categorías" used={kpis.categorias} limit={currentPlan.categorias} />
            <UsageBar label="Equipos" used={kpis.equipos} limit={currentPlan.equipos} />
            <UsageBar label="Jugadores" used={kpis.jugadores} limit={currentPlan.jugadores} />
            <UsageBar label="Usuarios" used={(kpis.entrenadores || 0) + 2 /* +admin + coordinator estimate */} limit={currentPlan.usuarios} />
          </div>
        </div>
      )}

      <div className="grid grid-tactical grid-cols-2 md:grid-cols-4 mb-10">
        <Kpi label="Categorías" value={kpis.categorias} icon={Layers} />
        <Kpi label="Equipos" value={kpis.equipos} icon={Trophy} />
        <Kpi label="Jugadores" value={kpis.jugadores} icon={Users} />
        <Kpi label="Entrenadores" value={kpis.entrenadores} icon={UserCog} />
        <Kpi label="Jornadas totales" value={kpis.jornadas} icon={ClipboardList} />
        <Kpi label="Partidos" value={kpis.partidos} icon={ClipboardList} />
        <Kpi label="Entrenamientos" value={kpis.entrenos} icon={ClipboardList} />
        <div className="p-6 flex items-center justify-center">
          <div className="overline text-primary">Actividad {new Date().getFullYear()}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <QuickCard title="Gestionar Equipos" desc="Da de alta equipos, asigna entrenadores y añade jugadores." to="/club/teams" tid="quick-teams" />
        <QuickCard title="Staff Técnico" desc="Invita coordinadores, entrenadores, directores deportivos y delegados." to="/club/staff" tid="quick-staff" />
        <QuickCard title="Evaluaciones" desc="Evalúa el desarrollo individual de cada jugador (trimestral, mensual o libre)." to="/evaluations" tid="quick-evaluations" />
        <QuickCard title="Registrar Jornada" desc="Anota resultados y estadísticas de un partido o entreno." to="/coach/jornadas/new" tid="quick-register-jornada" />
        <QuickCard title="Gestionar Categorías" desc="Configura las categorías de tu club y sus plantillas de evaluación." to="/club/categories" tid="quick-categories" />
        <QuickCard title="Plantillas de estadísticas" desc="Personaliza los indicadores por categoría (U6-U16)." to="/club/templates" tid="quick-templates" />
      </div>

      <div className="border border-border">
        <div className="p-4 border-b border-border">
          <div className="overline">Rendimiento por categoría</div>
        </div>
        <div className="grid grid-tactical grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
          {(data?.categorias || []).map((c) => (
            <div key={c.category.id} className="p-4">
              <div className="font-heading font-bold uppercase tracking-tight">{c.category.name}</div>
              <div className="overline mt-1">{c.category.template_key}</div>
              <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                <Row k="Equipos" v={c.teams_count} />
                <Row k="Jugadores" v={c.players_count} />
                <Row k="Jornadas" v={c.jornadas_count} />
                <Row k="GF/GC" v={`${c.gols_favor}/${c.gols_contra}`} />
              </div>
            </div>
          ))}
          {(!data?.categorias || data.categorias.length === 0) && (
            <div className="p-6 col-span-full text-center text-muted-foreground text-sm">
              Aún no hay categorías. Empieza en <Link to="/club/categories" className="text-primary link-underline">Categorías</Link>.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ClubLogoUploader({ club, onUpdated, canEdit }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [bust, setBust] = useState(0);
  // logo_url from backend already begins with "/api/clubs/..." — prepend
  // BACKEND_URL (without /api) so we don't get /api/api/... which returns 404.
  const src = club?.logo_url ? `${club.logo_url.startsWith("http") ? "" : BACKEND_URL}${club.logo_url.startsWith("/") ? "" : "/"}${club.logo_url}${bust ? (club.logo_url.includes("?") ? "&" : "?") + "cb=" + bust : ""}` : null;
  const handleFile = async (f) => {
    if (!f || !club?.id) return;
    if (!f.type.startsWith("image/")) { toast.error("Solo imágenes"); return; }
    if (f.size > 2 * 1024 * 1024) { toast.error("Máximo 2 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post(`/clubs/${club.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBust(Date.now());
      onUpdated?.(data.logo_url);
      toast.success("Logo actualizado");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al subir");
    } finally { setUploading(false); }
  };
  const handleDelete = async () => {
    if (!club?.id || !window.confirm("¿Eliminar el logo del club?")) return;
    try { await api.delete(`/clubs/${club.id}/logo`); onUpdated?.(null); toast.success("Logo eliminado"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };
  return (
    <div className="relative group flex-shrink-0" data-testid="club-logo-uploader">
      <div className="w-24 h-24 md:w-28 md:h-28 border-2 border-border flex items-center justify-center bg-white overflow-hidden">
        {src ? (
          <img src={src} alt={club?.name || "Logo"} className="w-full h-full object-contain p-1" />
        ) : (
          <Shield size={44} className="text-slate-400" />
        )}
      </div>
      {canEdit && (
        <>
          <input ref={inputRef} type="file" accept="image/*" className="hidden"
                 onChange={(e) => handleFile(e.target.files?.[0])} data-testid="club-logo-input" />
          <button
            data-testid="club-logo-upload-btn"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="absolute -bottom-2 -right-2 w-8 h-8 bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/80 disabled:opacity-50"
            title={src ? "Cambiar logo" : "Subir logo"}
          >
            <Camera size={14} />
          </button>
          {src && (
            <button
              data-testid="club-logo-delete-btn"
              onClick={handleDelete}
              className="absolute -top-2 -right-2 w-6 h-6 bg-black border border-red-400 text-red-400 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center hover:bg-red-400 hover:text-black"
              title="Eliminar logo"
            >
              <Trash2 size={11} />
            </button>
          )}
        </>
      )}
      {uploading && <div className="absolute inset-0 bg-black/70 flex items-center justify-center text-[10px] uppercase font-heading tracking-wider">Subiendo…</div>}
    </div>
  );
}

function Kpi({ label, value, icon: Icon }) {
  return (
    <div className="p-6">
      <div className="flex items-start justify-between">
        <div className="overline">{label}</div>
        <Icon size={14} className="text-muted-foreground" />
      </div>
      <div className="metric-number text-5xl mt-3">{value}</div>
    </div>
  );
}

function QuickCard({ title, desc, to, tid }) {
  return (
    <Link to={to} data-testid={tid} className="card-tactical p-5 flex items-start justify-between group">
      <div>
        <div className="font-heading font-bold uppercase tracking-tight text-lg">{title}</div>
        <div className="text-sm text-muted-foreground mt-1">{desc}</div>
      </div>
      <ArrowUpRight size={18} className="text-primary opacity-60 group-hover:opacity-100 transition-opacity" />
    </Link>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between border-b border-border/50 pb-1">
      <span className="text-muted-foreground">{k}</span>
      <span className="font-mono font-semibold">{v}</span>
    </div>
  );
}

function UsageBar({ label, used, limit }) {
  const isUnlimited = limit === null || limit === undefined;
  const pct = isUnlimited ? 0 : Math.min(100, Math.round((used / limit) * 100));
  const alert = !isUnlimited && pct >= 80;
  return (
    <div className="p-4">
      <div className="flex items-center justify-between overline mb-2">
        <span>{label}</span>
        <span className={`font-mono ${alert ? "text-destructive" : "text-primary"}`}>
          {used}{isUnlimited ? " · ∞" : ` / ${limit}`}
        </span>
      </div>
      <div className="h-1 bg-border">
        {!isUnlimited && <div className={`h-full ${alert ? "bg-destructive" : "bg-primary"}`} style={{ width: `${pct}%` }} />}
        {isUnlimited && <div className="h-full bg-emerald-400" style={{ width: "100%" }} />}
      </div>
    </div>
  );
}
